// src/components/trades/ProposeTradeTab.js - PHASE 6 ENHANCED
import React, { useState, useEffect } from 'react';
import { dynastyTheme } from '../../services/colorService';
import { ArrowRightLeft, Send, AlertCircle, Loader, TrendingUp } from 'lucide-react';
import { leaguesAPI } from '../../services/apiService';
import { formatApiError } from '../../services/errorUtils';
import PlayerFilterBar from './components/PlayerFilterBar';
import RosterTable from './components/RosterTable';
import TradeAssetsSummary from './components/TradeAssetsSummary';
import TradeAnalysisModal from './TradeAnalysisModal';
// DEFECT 2 Phase 1 — placement cascade reuses the canonical waiver-side
// components (DropDesignationModal + capacity analyzer). Per QB directive:
// reuse Phase A.4.5 components; do NOT rebuild.
import DropDesignationModal from '../modals/DropDesignationModal';
import { analyzeRosterCapacity, canClaimWaiverPlayer } from '../../utils/RosterCapacityUtils';

/**
 * ProposeTradeTab - PHASE 6: Advanced Trade Builder with VERTICAL LAYOUT
 * 
 * Features:
 * - Full roster tables with narrow 2-row format (season + 14-day stats)
 * - Trade-specific columns matching myRoster screen styling
 * - Dynamic stat columns from league settings
 * - Position filtering (All/Batters/Pitchers + sub-positions)
 * - Player search
 * - Collapsible roster sections (Active/Bench/DL/Minors)
 * - Draft picks section
 * - Cash trading support
 * 
 * Layout (VERTICAL STACK):
 * +-------------------------------------------+
 * |   MY TEAM (full width)                    |
 * | [Filter/Search] [Collapsible Sections]    |
 * +-------------------------------------------+
 * |   TRADE CENTER (full width)               |
 * | [I GIVE] [Players/Picks/Cash] [I GET]     |
 * +-------------------------------------------+
 * |   THEIR TEAM (full width)                 |
 * | [Filter/Search] [Collapsible Sections]    |
 * +-------------------------------------------+
 */
const ProposeTradeTab = ({
  leagueId,
  userTeam,
  league,
  onTradeProposed,
  preSelectedPicks = [],
  tradeState,
  onTradeStateChange,
  onSubmitReady,
  isCommissionerMode = false,
  commissionerTeamId = null,
}) => {
  // Use trade state from parent (TradeCenter) for persistence
  const { selectedTeamId, iGive, iGet, notes } = tradeState;

  // Helper to update trade state — uses functional updater to avoid stale closures.
  // Without this, two state updates in the same render cycle (e.g., useEffects on mount)
  // would overwrite each other because both read the same stale tradeState from closure.
  const setSelectedTeamId = (value) => {
    onTradeStateChange(prev => ({ ...prev, selectedTeamId: value }));
  };
  const setIGive = (valueOrFn) => {
    onTradeStateChange(prev => ({
      ...prev,
      iGive: typeof valueOrFn === 'function' ? valueOrFn(prev.iGive) : valueOrFn
    }));
  };
  const setIGet = (valueOrFn) => {
    onTradeStateChange(prev => ({
      ...prev,
      iGet: typeof valueOrFn === 'function' ? valueOrFn(prev.iGet) : valueOrFn
    }));
  };
  const setNotes = (value) => {
    onTradeStateChange(prev => ({ ...prev, notes: value }));
  };
  
  // Local state (not persisted across tabs)
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [myCashBalance, setMyCashBalance] = useState(null);
  const [theirCashBalance, setTheirCashBalance] = useState(null);
  const [myRoster, setMyRoster] = useState([]);
  const [myPicks, setMyPicks] = useState([]);
  const [theirRoster, setTheirRoster] = useState([]);
  const [theirPicks, setTheirPicks] = useState([]);

  // Filtering state - My Team
  const [myPositionFilter, setMyPositionFilter] = useState('hitting');
  const [mySearchTerm, setMySearchTerm] = useState('');

  // Filtering state - Their Team
  const [opponentPositionFilter, setOpponentPositionFilter] = useState('hitting');
  const [opponentSearchTerm, setOpponentSearchTerm] = useState('');

  // Trade Analysis Modal state
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);

  // Load teams on mount
  useEffect(() => {
    if (leagueId) {
      loadTeams();
      loadMyAssets();
    }
  }, [leagueId, commissionerTeamId, isCommissionerMode]);

  // Load their assets when team selected
  useEffect(() => {
    if (selectedTeamId) {
      loadTheirAssets(selectedTeamId);
    } else {
      setTheirRoster([]);
      setTheirPicks([]);
      // Clear their side of trade when team deselected
      setIGet({ players: [], picks: [], cash: 0 });
    }
  }, [selectedTeamId]);

  // Sync pre-selected picks from Draft Picks tab
  useEffect(() => {
    if (preSelectedPicks.length > 0) {
      setIGive(prev => {
        const existingPickIds = prev.picks.map(p => p.pick_id);
        const newPicks = preSelectedPicks.filter(p => !existingPickIds.includes(p.pick_id));
        if (newPicks.length > 0) {
          return { ...prev, picks: [...prev.picks, ...newPicks] };
        }
        return prev;
      });
    }
  }, [preSelectedPicks]);

  const loadTeams = async () => {
    try {
      const response = await leaguesAPI.getLeagueTeams(leagueId);
      if (response.success) {
        // Filter out user's own team - users cannot trade with themselves
        const otherTeams = response.teams.filter(t => {
          // Filter out the user's team by checking is_user_team flag or team_id match
          return !t.is_user_team && t.team_id !== userTeam?.team_id;
        });
        setTeams(otherTeams);
      }
    } catch (err) {
      console.error('Error loading teams:', err);
    }
  };

  const loadMyAssets = async () => {
    try {
      setLoading(true);

      // In commissioner mode, load the selected team's roster
      const rosterOptions = isCommissionerMode && commissionerTeamId
        ? { commissioner_action: true, target_team_id: commissionerTeamId }
        : {};
      const rosterResponse = await leaguesAPI.getMyRosterCanonical(leagueId, rosterOptions);
      if (rosterResponse.success) {
        setMyRoster(rosterResponse.players || []);
      }
      
      // Only load draft picks if NOT in offline draft mode AND have valid team_id
      const draftType = league?.draft_type || league?.draft_settings?.type || league?.season_setup?.draft_type;
      const isOfflineDraft = draftType === 'offline';
      const effectiveTeamId = isCommissionerMode && commissionerTeamId ? commissionerTeamId : userTeam?.team_id;
      const hasValidTeamId = effectiveTeamId && effectiveTeamId !== 'commissioner-team';

      if (hasValidTeamId && !isOfflineDraft) {
        try {
          // §4.5.24: tradeable_only=true — backend returns only tradeable picks
          // and now provides is_owned (previously absent, so this filter was a no-op).
          const picksResponse = await leaguesAPI.getTeamDraftPicks(leagueId, effectiveTeamId, true);
          if (picksResponse.success) {
            const ownedPicks = picksResponse.picks.filter(p => p.is_owned);
            setMyPicks(ownedPicks);
          }
        } catch (err) {
          console.warn('Draft picks not available:', err);
          setMyPicks([]);
        }

        try {
          const balanceResponse = await leaguesAPI.getTeamCashBalance(leagueId, effectiveTeamId);
          if (balanceResponse.success) {
            setMyCashBalance(balanceResponse.cash_balance);
          }
        } catch (err) {
          console.warn('Cash balance not available:', err);
          setMyCashBalance(null);
        }
      } else if (isOfflineDraft) {
        // Offline draft - no draft picks to trade
        console.log('Offline draft mode - draft picks are not tradeable');
        setMyPicks([]);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading my assets:', err);
      setError('Failed to load your team assets');
      setLoading(false);
    }
  };

  const loadTheirAssets = async (teamId) => {
    try {
      setLoading(true);
      
      // Load their roster
      const rosterResponse = await leaguesAPI.getTeamRosterCanonical(leagueId, teamId);
      if (rosterResponse.success) {
        setTheirRoster(rosterResponse.players || []);
      }
      
      // Only load draft picks if NOT in offline draft mode
      const draftType = league?.draft_type || league?.draft_settings?.type || league?.season_setup?.draft_type;
      const isOfflineDraft = draftType === 'offline';
      
      if (!isOfflineDraft) {
        // Draft picks are only tradeable in live/snake drafts, not offline
        try {
          // §4.5.24: tradeable_only=true — backend returns only tradeable picks
          // and now provides is_owned (previously absent, so this filter was a no-op).
          const picksResponse = await leaguesAPI.getTeamDraftPicks(leagueId, teamId, true);
          if (picksResponse.success) {
            const ownedPicks = picksResponse.picks.filter(p => p.is_owned);
            setTheirPicks(ownedPicks);
          }
        } catch (err) {
          console.warn('Draft picks not available (offline draft or not implemented):', err);
          setTheirPicks([]); // Offline draft or feature not deployed yet
        }
      } else {
        // Offline draft - no draft picks to trade
        setTheirPicks([]);
      }
      
      // Load their cash balance - Phase 7
      try {
        const balanceResponse = await leaguesAPI.getTeamCashBalance(leagueId, teamId);
        if (balanceResponse.success) {
          setTheirCashBalance(balanceResponse.cash_balance);
        }
      } catch (err) {
        console.warn('Cash balance not available (Phase 7 not deployed?):', err);
        setTheirCashBalance(null);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading their assets:', err);
      setError('Failed to load opponent team assets');
      setLoading(false);
    }
  };

  // ============= TRADE ACTIONS (TOGGLE BEHAVIOR) =============

  const handleToggleMyPlayer = (player) => {
    const playerId = player.ids?.mlb;
    setIGive(prev => ({
      ...prev,
      players: prev.players.find(p => p.ids?.mlb === playerId)
        ? prev.players.filter(p => p.ids?.mlb !== playerId)
        : [...prev.players, player]
    }));
  };

  const handleToggleMyPick = (pick) => {
    setIGive(prev => ({
      ...prev,
      picks: prev.picks.find(p => p.pick_id === pick.pick_id)
        ? prev.picks.filter(p => p.pick_id !== pick.pick_id)
        : [...prev.picks, pick]
    }));
  };

  const handleToggleTheirPlayer = (player) => {
    const playerId = player.ids?.mlb;
    setIGet(prev => ({
      ...prev,
      players: prev.players.find(p => p.ids?.mlb === playerId)
        ? prev.players.filter(p => p.ids?.mlb !== playerId)
        : [...prev.players, player]
    }));
  };

  const handleToggleTheirPick = (pick) => {
    setIGet(prev => ({
      ...prev,
      picks: prev.picks.find(p => p.pick_id === pick.pick_id)
        ? prev.picks.filter(p => p.pick_id !== pick.pick_id)
        : [...prev.picks, pick]
    }));
  };

  // Remove handlers for trade bar
  const removePlayerFromGive = (playerId) => {
    setIGive(prev => ({
      ...prev,
      players: prev.players.filter(p => p.ids.mlb !== playerId)
    }));
  };

  const removePickFromGive = (pickId) => {
    setIGive(prev => ({
      ...prev,
      picks: prev.picks.filter(p => p.pick_id !== pickId)
    }));
  };

  const removePlayerFromGet = (playerId) => {
    setIGet(prev => ({
      ...prev,
      players: prev.players.filter(p => p.ids.mlb !== playerId)
    }));
  };

  const removePickFromGet = (pickId) => {
    setIGet(prev => ({
      ...prev,
      picks: prev.picks.filter(p => p.pick_id !== pickId)
    }));
  };

  // Cash handlers - Phase 7: Add validation
  const handleGiveCashChange = (amount) => {
    const numAmount = parseFloat(amount) || 0;

    if (myCashBalance !== null && numAmount > myCashBalance) {
      setError(`You only have ${myCashBalance.toFixed(2)} available`);
      return;
    }

    setError(null);
    setIGive(prev => ({ ...prev, cash: numAmount }));
  };

  const handleGetCashChange = (amount) => {
    const numAmount = parseFloat(amount) || 0;

    if (theirCashBalance !== null && numAmount > theirCashBalance) {
      setError(`They only have ${theirCashBalance.toFixed(2)} available`);
      return;
    }

    setError(null);
    setIGet(prev => ({ ...prev, cash: numAmount }));
  };

  // ============= VALIDATION & SUBMIT =============

  const validateTrade = () => {
    if (!selectedTeamId) {
      return 'Please select a team to trade with';
    }

    const givingTotal = iGive.players.length + iGive.picks.length + (iGive.cash > 0 ? 1 : 0);
    const gettingTotal = iGet.players.length + iGet.picks.length + (iGet.cash > 0 ? 1 : 0);

    if (givingTotal === 0) {
      return 'You must give at least one asset';
    }

    if (gettingTotal === 0) {
      return 'You must receive at least one asset';
    }
    
    // Phase 7: Validate cash balances
    if (myCashBalance !== null && iGive.cash > myCashBalance) {
      return `Insufficient funds: You only have ${myCashBalance.toFixed(2)} available`;
    }
    
    if (theirCashBalance !== null && iGet.cash > theirCashBalance) {
      return `Insufficient funds: They only have ${theirCashBalance.toFixed(2)} available`;
    }

    return null;
  };

  // DEFECT 2 Phase 1 — placement cascade state. When the proposer is RECEIVING
  // players in this trade (iGet.players non-empty), we fire a sequential
  // DropDesignationModal cascade — one modal per incoming player — to gather
  // the proposer_placements array sent to the backend.
  const [placementCascade, setPlacementCascade] = useState({
    active: false,
    currentIdx: 0,
    accumulated: [],
  });

  const myCapacityAnalysis = React.useMemo(() => {
    if (!league || !myRoster || myRoster.length === 0) return null;
    try {
      return analyzeRosterCapacity(league, myRoster);
    } catch (e) {
      console.warn('Trade placement: capacity analyzer failed', e);
      return null;
    }
  }, [league, myRoster]);

  // Convert DropDesignationModal's onConfirm(dropPlayerId) result into a
  // backend placement object. dropPlayerId === null → action='bench'; non-null
  // → action='replace' (target_slot derived backend-side from the dropped
  // player's current slot per _compose_placement_plan).
  const buildPlacementFromModal = (incomingPlayer, dropLeaguePlayerId) => {
    const incoming_player_mlb_id = incomingPlayer?.ids?.mlb;
    const incoming_player_league_player_id = incomingPlayer?.ids?.league_player;
    if (dropLeaguePlayerId) {
      return {
        incoming_player_mlb_id,
        incoming_player_league_player_id,
        action: 'replace',
        target_slot: null,  // backend derives from drop player's slot
        drop_league_player_id: dropLeaguePlayerId,
      };
    }
    return {
      incoming_player_mlb_id,
      incoming_player_league_player_id,
      action: 'bench',
      target_slot: null,
      drop_league_player_id: null,
    };
  };

  // Actual POST. Separated so the cascade-finalize path and the no-cascade
  // (no incoming players) path share submission logic.
  const submitTradeWithPlacements = async (proposerPlacements) => {
    try {
      setSubmitting(true);
      setError(null);

      const tradeData = {
        receiving_team_id: selectedTeamId,
        proposing_team_gives: {
          picks: iGive.picks.map(p => ({
            year: p.year,
            round: p.round,
            pick_id: p.pick_id,
            pick_type: p.pick_type || 'regular'
          })),
          players: iGive.players.map(p => p.ids.mlb),
          cash: iGive.cash || 0
        },
        receiving_team_gives: {
          picks: iGet.picks.map(p => ({
            year: p.year,
            round: p.round,
            pick_id: p.pick_id,
            pick_type: p.pick_type || 'regular'
          })),
          players: iGet.players.map(p => p.ids.mlb),
          cash: iGet.cash || 0
        },
        notes: notes || null,
        proposer_placements: proposerPlacements && proposerPlacements.length > 0 ? proposerPlacements : null,
      };

      const response = isCommissionerMode
        ? await leaguesAPI.commissionerExecuteTrade(leagueId, {
            proposing_team_id: commissionerTeamId || userTeam?.team_id,
            receiving_team_id: selectedTeamId,
            proposing_team_gives: tradeData.proposing_team_gives,
            receiving_team_gives: tradeData.receiving_team_gives,
            commissioner_notes: tradeData.notes
          })
        : await leaguesAPI.proposeTrade(leagueId, tradeData);

      if (response.success) {
        setMyPositionFilter('hitting');
        setMySearchTerm('');
        setOpponentPositionFilter('hitting');
        setOpponentSearchTerm('');
        if (onTradeProposed) {
          onTradeProposed();
        }
      } else {
        setError(response.message || 'Failed to propose trade');
      }
    } catch (err) {
      console.error('Error proposing trade:', err);
      const baseMessage = formatApiError(err, 'Failed to propose trade');
      const isDeadlineError = typeof baseMessage === 'string' && /trade deadline/i.test(baseMessage);
      const errorMessage = isDeadlineError
        ? `⚠️ Trades aren't allowed after the trade deadline. Contact your commissioner if you believe this is an error.`
        : baseMessage;
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitTrade = async () => {
    const validationError = validateTrade();
    if (validationError) {
      setError(validationError);
      return;
    }

    // DEFECT 2 Phase 1: if the PROPOSER is receiving players (iGet.players
    // non-empty), kick off the placement modal cascade. Each modal collects
    // ONE placement; on the final modal, the cascade auto-submits with the
    // accumulated proposer_placements.
    // Commissioner-execute path skips the cascade — commissioner already
    // had to designate placements via a different surface (out of scope for
    // this defect; commissioner-execute is the bypass-proposal/accept path).
    if (!isCommissionerMode && iGet.players.length > 0) {
      setPlacementCascade({
        active: true,
        currentIdx: 0,
        accumulated: [],
      });
      return;
    }

    // No incoming players for the proposer (or commissioner mode) → submit
    // direct with no placements (backend's Phase 0 auto-assign covers the
    // proposer side if applicable).
    await submitTradeWithPlacements(null);
  };

  // Cascade modal confirm — called from DropDesignationModal.onConfirm(dropId)
  const handleCascadeConfirm = async (dropLeaguePlayerId) => {
    const incomingPlayer = iGet.players[placementCascade.currentIdx];
    const placement = buildPlacementFromModal(incomingPlayer, dropLeaguePlayerId);
    const newAccumulated = [...placementCascade.accumulated, placement];
    const nextIdx = placementCascade.currentIdx + 1;

    if (nextIdx >= iGet.players.length) {
      // Cascade complete — submit
      setPlacementCascade({ active: false, currentIdx: 0, accumulated: [] });
      await submitTradeWithPlacements(newAccumulated);
    } else {
      // Advance to next incoming player's modal
      setPlacementCascade({
        active: true,
        currentIdx: nextIdx,
        accumulated: newAccumulated,
      });
    }
  };

  const handleCascadeClose = () => {
    // User abandoned the cascade — cancel the submit entirely.
    setPlacementCascade({ active: false, currentIdx: 0, accumulated: [] });
  };

  // Pass handleSubmitTrade up to parent so it can be wired to external buttons
  useEffect(() => {
    if (onSubmitReady) {
      onSubmitReady(handleSubmitTrade);
    }
  }, [handleSubmitTrade]);

  // ============= RENDER =============

  // Show initial loading only for first mount
  if (loading && myRoster.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-teal-400" />
        <span className="ml-3 text-gray-300">Loading your assets...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Error Display */}
      {error && (
        <div className={`p-4 rounded-lg border border-red-500/50 ${dynastyTheme.classes.bg.darkCard} flex items-start gap-3`}>
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-300">{error}</div>
        </div>
      )}

      {/* VERTICAL STACKED LAYOUT */}
      <div className="space-y-4">
        
        {/* TOP PANEL: MY TEAM - ALWAYS VISIBLE */}
        <div className={`${dynastyTheme.classes.bg.darkCard} rounded-lg border ${dynastyTheme.classes.border.neutral} p-4`}>
          <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.primary} mb-4`}>
            MY TEAM
          </h3>
          
          <PlayerFilterBar
            selectedPosition={myPositionFilter}
            searchTerm={mySearchTerm}
            onPositionChange={setMyPositionFilter}
            onSearchChange={setMySearchTerm}
          />
          
          <RosterTable
            players={myRoster}
            picks={myPicks}
            selectedPlayers={iGive.players.map(p => p.ids?.mlb)}
            selectedPicks={iGive.picks.map(p => p.pick_id)}
            onPlayerToggle={handleToggleMyPlayer}
            onPickToggle={handleToggleMyPick}
            positionFilter={myPositionFilter}
            searchTerm={mySearchTerm}
            title="Available to Trade"
            league={league}
          />
        </div>

        {/* MIDDLE PANEL: TRADE CENTER - FULL WIDTH */}
        <div className="w-full">
          <TradeAssetsSummary
            iGive={iGive}
            iGet={iGet}
            onRemoveGivePlayer={removePlayerFromGive}
            onRemoveGivePick={removePickFromGive}
            onRemoveGetPlayer={removePlayerFromGet}
            onRemoveGetPick={removePickFromGet}
            onGiveCashChange={handleGiveCashChange}
            onGetCashChange={handleGetCashChange}
            myCashBalance={myCashBalance}
            theirCashBalance={theirCashBalance}
            league={league}
          />
        </div>

        {/* TEAM SELECTOR - Above Their Team */}
        <div className={`p-4 ${dynastyTheme.classes.bg.darkCard} rounded-lg border ${dynastyTheme.classes.border.neutral}`}>
          <label className={`block text-sm font-bold ${dynastyTheme.classes.text.white} mb-2`}>
            Select Team to Trade With
          </label>
          <select
            value={selectedTeamId}
            onChange={(e) => setSelectedTeamId(e.target.value)}
            className={`w-full px-4 py-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} focus:border-teal-400 focus:outline-none`}
          >
            <option value="">-- Choose a team --</option>
            {teams.map(team => (
              <option key={team.team_id} value={team.team_id}>
                {team.team_name} ({team.owner_name})
              </option>
            ))}
          </select>
        </div>

        {/* BOTTOM PANEL: THEIR TEAM - CONDITIONAL ON TEAM SELECTION */}
        <div className={`${dynastyTheme.classes.bg.darkCard} rounded-lg border ${dynastyTheme.classes.border.neutral} p-4`}>
          <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.primary} mb-4`}>
            THEIR TEAM
          </h3>
          
          {!selectedTeamId ? (
            <div className={`text-center py-12 ${dynastyTheme.classes.text.neutralLight}`}>
              <ArrowRightLeft className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Select a team above to view their assets</p>
            </div>
          ) : loading ? (
            <div className="text-center py-8">
              <Loader className="w-6 h-6 animate-spin text-teal-400 mx-auto" />
            </div>
          ) : (
            <>
              <PlayerFilterBar
                selectedPosition={opponentPositionFilter}
                searchTerm={opponentSearchTerm}
                onPositionChange={setOpponentPositionFilter}
                onSearchChange={setOpponentSearchTerm}
              />
              
              <RosterTable
                players={theirRoster}
                picks={theirPicks}
                selectedPlayers={iGet.players.map(p => p.ids?.mlb)}
                selectedPicks={iGet.picks.map(p => p.pick_id)}
                onPlayerToggle={handleToggleTheirPlayer}
                onPickToggle={handleToggleTheirPick}
                positionFilter={opponentPositionFilter}
                searchTerm={opponentSearchTerm}
                title="Available to Trade"
                league={league}
              />
            </>
          )}
        </div>
      </div>

      {/* Notes Section - Show when team selected */}
      {selectedTeamId && (
        <div className={`p-4 ${dynastyTheme.classes.bg.darkCard} rounded-lg border ${dynastyTheme.classes.border.neutral}`}>
          <label className={`block text-sm font-bold ${dynastyTheme.classes.text.white} mb-2`}>
            Trade Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add a message to the other team..."
            rows={3}
            className={`w-full px-4 py-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} focus:border-teal-400 focus:outline-none resize-none`}
          />
        </div>
      )}

      {/* Action Buttons - Show when team selected */}
      {selectedTeamId && (
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setShowAnalysisModal(true)}
            disabled={!selectedTeamId || (iGive.players.length === 0 && iGet.players.length === 0)}
            className={`
              ${!selectedTeamId || (iGive.players.length === 0 && iGet.players.length === 0)
                ? 'bg-gray-600 cursor-not-allowed text-gray-400 px-6 py-3 rounded-lg font-bold'
                : dynastyTheme.utils.getComponent('button', 'secondary', 'lg')
              }
              flex items-center gap-2
            `}
          >
            <TrendingUp className="w-5 h-5" />
            Analyze Trade
          </button>
          
          <button
            onClick={handleSubmitTrade}
            disabled={submitting}
            className={`
              ${submitting
                ? 'bg-gray-600 cursor-not-allowed text-gray-400 px-6 py-3 rounded-lg font-bold'
                : dynastyTheme.utils.getComponent('button', 'primary', 'lg')
              }
              flex items-center gap-2
            `}
          >
            {submitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Propose Trade
              </>
            )}
          </button>
        </div>
      )}

      {/* Trade Analysis Modal */}
      <TradeAnalysisModal
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        leagueId={leagueId}
        receivingTeamId={selectedTeamId}
        proposingTeamGives={iGive}
        receivingTeamGives={iGet}
      />

      {/* DEFECT 2 Phase 1 — placement cascade. Fires AFTER user clicks
          "Propose Trade" when iGet.players is non-empty. One modal per
          incoming player, sequentially. On final confirm, the trade is
          submitted with the accumulated proposer_placements array. */}
      {placementCascade.active && iGet.players[placementCascade.currentIdx] && (() => {
        const incoming = iGet.players[placementCascade.currentIdx];
        const claimResult = myCapacityAnalysis
          ? canClaimWaiverPlayer(incoming, myCapacityAnalysis)
          : { hasActiveSlot: false, hasBenchSlot: false, requiresDrop: true };
        const isRosterFull = !!claimResult.requiresDrop;
        console.log('[TRADE-ACCEPT-DEBUG]', { hasLeague: !!league, incoming_position: incoming?.info?.position, incoming_eligible: incoming?.eligible_positions, position_requirements: league?.roster_settings?.position_requirements, bench_slots: league?.roster_settings?.bench_slots, capacityAnalysis: myCapacityAnalysis, claimResult }); // REVERT AFTER TRADE DEBUG
        return (
          <DropDesignationModal
            isOpen={true}
            onClose={handleCascadeClose}
            onConfirm={handleCascadeConfirm}
            playerBeingAdded={incoming}
            currentRoster={myRoster || []}
            leagueId={leagueId}
            context="trade_placement"
            isRosterFull={isRosterFull}
            hasActiveSlot={claimResult.hasActiveSlot}
            hasBenchSlot={claimResult.hasBenchSlot}
            waiverPriority={null}
            totalTeams={null}
            waiverExpiresAt={null}
          />
        );
      })()}
    </div>
  );
};

export default ProposeTradeTab;
